import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { tileLayer, latLng, marker, icon, Icon } from 'leaflet';
import { Lightbox } from 'ngx-lightbox';
import { NgForm, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { OrganizationsService } from '../organizations.service';
import { Organization } from 'src/app/shared/models/organization.model';
import * as FormValidation from 'src/app/shared/_helpers/get-form-validation-errors';
import { environment } from 'src/environments/environment';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { SharedService } from 'src/app/shared/shared.service';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { EditorChangeContent } from 'ngx-quill';

@Component({
    selector: 'app-update-organization',
    templateUrl: './update-organization.component.html',
    styleUrls: ['./update-organization.component.css'],
    standalone: false
})
export class UpdateOrganizationComponent implements OnInit {

  @ViewChild('orgInfoForm') form: NgForm;
  @ViewChild('newMapLatitude') newMapLatitude: ElementRef;
  @ViewChild('newMapLongitude') newMapLongitude: ElementRef;

  private defaultIcon: Icon = icon({
    iconUrl: 'assets/leaflet/marker-icon.png',
    shadowUrl: 'assets/leaflet/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });

  public options = {
    layers: [
      tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '...' })
    ],
    zoom: 8,
    center: latLng(46.6553557, 10.8692822)
  };
  public layers = [];
  public currentOrgLanguage = 'it';
  public formGroup: FormGroup;
  public languageToString = ({
    'it': 'Italian - IT-it',
    'de': 'German - DE-de',
    'en': 'English - EN-en',
  });
  public icons: { [name: string]: any } = {
    'faLink': faLink
  };
  public imagePath: any;
  public validationError: FormValidation.AllValidationErrors;
  public organizationType: number;
  public fileSizeError = false;

  private _organization: Organization;
  private _newLocation = ({
    'latitude': undefined,
    'longitude': undefined,
  });
  private _imageToUpload: File = null;

  public remainingText_it;
  public remainingText_de;
  public remainingText_en;
  public maxLengthText = 5000;
  private _errorTextLenght = false;

  constructor(private _activatedRoute: ActivatedRoute, private _router: Router, private _organizationsService: OrganizationsService,
    private _formBuilder: FormBuilder, private _lightbox: Lightbox, private _toastService: ToastService,
    private _sharedService: SharedService) {
    this.formGroup = this._formBuilder.group({
      id: [''],
      name_it: ['', <any>Validators.required],
      name_de: ['', <any>Validators.required],
      name_en: [''],
      imagePath: [''],
      address_de: [''],
      address_it: [''],
      address_en: [''],
      description_de: [''],
      description_it: [''],
      description_en: [''],
      latitude: [''],
      longitude: [''],
      phone: [''],
      webPage: [''],
      webPage_en: [''],
      webPage_de: [''],
      email: [''],
      seoCode: ['']
    });

    this._toastService.remove();
  }

  ngOnInit(): void {

    const urlPramas = this._activatedRoute.snapshot.params;
    if (!urlPramas.orgId) {
      this._router.navigate(['/']);
    }
    this._organizationsService.getOrganization(urlPramas.orgId).subscribe(requestResult => {
      console.log('request-result', requestResult);
      this._organization = requestResult;
      this.formGroup.patchValue(this._organization);
      if (this._organization.latitude && this._organization.longitude) {
        this._addMarker(this._organization.latitude, this._organization.longitude);
      }
      if (this._organization.imagePath) {
        this.imagePath = environment.imageUrl + this._organization.id + '/' + this._organization.imagePath;
      }
      // tslint:disable-next-line:radix
      this.organizationType = parseInt(localStorage.getItem('orgId'));
      this._sharedService.organizationNameSubject.next({ 'de': this._organization.name_de, 'it': this._organization.name_it });

      this.countTextLength('it', requestResult.description_it);
      this.countTextLength('en', requestResult.description_en);
      this.countTextLength('de', requestResult.description_de);

    }, requestError => {
      console.log(requestError);
    });
  }

  onLanguageChange(language: string) {
    this.currentOrgLanguage = language;
    console.log('_organization: ', this._organization.description_it);
    this.countTextLength('it', this.formGroup.get('description_it').value);
    this.countTextLength('en', this.formGroup.get('description_en').value);
    this.countTextLength('de', this.formGroup.get('description_de').value);
  }

  submitForm() {
    if (!this._errorTextLenght) {
      if (this.form.valid) {
        if (this._newLocation.latitude && this._newLocation.longitude) {
          this.formGroup.patchValue({ 'latitude': this._newLocation.latitude });
          this.formGroup.patchValue({ 'longitude': this._newLocation.longitude });
        }
        this._organizationsService.submitOrganizationUpdate(this.form.value).subscribe(result => {
          if (this._imageToUpload) {
            const imageFileNameArray = this._imageToUpload.name.split('.');
            const imageNameFinale = imageFileNameArray[imageFileNameArray.length - 1].toLocaleLowerCase();
            const imageName = Math.random().toString(36).slice(-8) + '.' + imageNameFinale;
            this._organizationsService.submitOrganizationImage(this._imageToUpload, imageName, this._organization.id)
              .subscribe(requestResult => {
                console.log(requestResult);
              }, responseError => {
                // TODO: error toaster
                console.error('Image Upload :: ', responseError);
              });
          }
          this._toastService.show('', {
            classname: 'bg-success text-light',
            delay: 2000,
            autohide: true,
          });
        }, error => {
          console.error('Organization Upload :: ', error);
        });
      } else {
        this.validationError = FormValidation.getFormValidationErrors(this.formGroup.controls).shift();
      }
    } else {
      this._toastService.show('GENERAL.TOAST.errorQuillMaxLenght', {
        classname: 'bg-danger text-light',
        delay: 3000,
        autohide: true,
      });
    }
  }

  onMapResetClick() {
    this.newMapLatitude.nativeElement.value = '';
    this.newMapLongitude.nativeElement.value = '';
    this._addMarker(this._organization.latitude, this._organization.longitude);
  }

  onMapClick(event: any) {
    this._addMarker(event.latlng.lat.toString(), event.latlng.lng.toString());
  }


  handleImageFileInput(event: any) {
    const files = event.target.files;
    this.fileSizeError = false;
    if (files.length === 0) { return; }
    if (files.length > 1) { return; }
    const mimeType = files[0].type;
    if (mimeType.match(/image\/*/) == null) {
      // return this._toastr.warning(this._translate.instant('event.advertiseEvent.imageError'));
      return;
    }
    if (files[0].size > 9500000) {
      this.fileSizeError = true;
      return;
    }
    const reader = new FileReader();
    this._imageToUpload = files.item(0);
    reader.readAsDataURL(files[0]);
    reader.onload = (_event) => {
      this.imagePath = reader.result;
    };
  }

  openLightbox() {
    this._lightbox.open([{
      src: this.imagePath,
      caption: '',
      thumb: this.imagePath
    }], 0);
  }

  _addMarker(lat: string, lng: string) {
    this._newLocation.latitude = lat;
    this._newLocation.longitude = lng;
    this.layers = [];
    this.layers.push(marker([parseFloat(lat), parseFloat(lng)], { icon: this.defaultIcon }));
    this.options.center = latLng(parseFloat(lat), parseFloat(lng));
  }

  deleteImage() {
    this._organizationsService.deleteImage(this._organization.id.toString()).subscribe(result => {
      this.imagePath = undefined;
    });
  }

  setNewMapLocation() {
    const newMapLatitude = this.newMapLatitude.nativeElement.value;
    const newMapLongitude = this.newMapLongitude.nativeElement.value;
    if (newMapLatitude && newMapLongitude) {
      this._addMarker(newMapLatitude, newMapLongitude);
    }
  }

  changedEditor(lang: string, event: EditorChangeContent) {
    this.countTextLength(lang, event.html, 1);
   }

  countTextLength(lang: string, myText: string, mode: number = 0) {
    if (!myText && mode === 0) { myText = ''; }
    if (myText) {
      if (lang === 'it') {
        this.remainingText_it = this.maxLengthText - myText.length;
      }
      if (lang === 'en') {
        this.remainingText_en = this.maxLengthText - myText.length;
      }
      if (lang === 'de') {
        this.remainingText_de = this.maxLengthText - myText.length;
      }
      this._errorTextLenght  = (this.remainingText_it < 0) || (this.remainingText_en < 0) || (this.remainingText_de < 0);
    }
  }
}
